'use client';

import { useEffect, useMemo, useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { es } from 'date-fns/locale';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface DateInputWithCalendarProps {
  id?: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  minDate?: Date;
  /** Inclusive upper bound; dates after this day are disabled. */
  maxDate?: Date;
  /** Extra rule on top of min/max: rejected days are disabled in the calendar and ignored when typed. */
  isDateAllowed?: (date: Date) => boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string; // default: dd/mm/aaaa
}

function toLocalMidnight(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isValidLocalYmd(year: number, month: number, day: number): boolean {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  if (year < 1000 || year > 9999) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  const d = new Date(year, month - 1, day, 0, 0, 0, 0);
  return (
    d.getFullYear() === year &&
    d.getMonth() === month - 1 &&
    d.getDate() === day
  );
}

function formatDdMmYyyy(value: Date): string {
  const dd = String(value.getDate()).padStart(2, '0');
  const mm = String(value.getMonth() + 1).padStart(2, '0');
  const yyyy = String(value.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

function parseDdMmYyyy(input: string): Date | null {
  const s = input.trim();
  if (!s) return null;
  const m = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(s);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (!isValidLocalYmd(year, month, day)) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

export function DateInputWithCalendar({
  id,
  value,
  onChange,
  minDate,
  maxDate,
  isDateAllowed,
  disabled = false,
  className,
  placeholder = 'dd/mm/aaaa',
}: DateInputWithCalendarProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string>('');

  const normalizedValue = useMemo(() => (value ? toLocalMidnight(value) : null), [value]);
  const normalizedMin = useMemo(() => (minDate ? toLocalMidnight(minDate) : null), [minDate]);
  const normalizedMax = useMemo(() => (maxDate ? toLocalMidnight(maxDate) : null), [maxDate]);

  useEffect(() => {
    setText(normalizedValue ? formatDdMmYyyy(normalizedValue) : '');
  }, [normalizedValue?.getTime()]);

  const commit = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      onChange(null);
      return;
    }

    const parsed = parseDdMmYyyy(trimmed);
    if (!parsed) {
      setText(normalizedValue ? formatDdMmYyyy(normalizedValue) : '');
      return;
    }

    const d = toLocalMidnight(parsed);
    if (normalizedMin && d.getTime() < normalizedMin.getTime()) {
      setText(normalizedValue ? formatDdMmYyyy(normalizedValue) : '');
      return;
    }
    if (normalizedMax && d.getTime() > normalizedMax.getTime()) {
      setText(normalizedValue ? formatDdMmYyyy(normalizedValue) : '');
      return;
    }
    if (isDateAllowed && !isDateAllowed(d)) {
      setText(normalizedValue ? formatDdMmYyyy(normalizedValue) : '');
      return;
    }

    onChange(d);
  };

  return (
    <div className={cn('relative', className)}>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            (e.currentTarget as HTMLInputElement).blur();
          }
        }}
        disabled={disabled}
        className={cn('pr-10', disabled && 'opacity-60')}
      />

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-muted-foreground"
            title="Abrir calendario"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(true);
            }}
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={normalizedValue ?? undefined}
            defaultMonth={normalizedValue ?? new Date()}
            locale={es}
            onSelect={(d) => {
              if (!d) return;
              const picked = toLocalMidnight(d);
              if (normalizedMin && picked.getTime() < normalizedMin.getTime()) return;
              if (normalizedMax && picked.getTime() > normalizedMax.getTime()) return;
              if (isDateAllowed && !isDateAllowed(picked)) return;
              onChange(picked);
              setOpen(false);
            }}
            disabled={(d) => {
              if (disabled) return true;
              const x = toLocalMidnight(d);
              if (normalizedMin && x.getTime() < normalizedMin.getTime()) return true;
              if (normalizedMax && x.getTime() > normalizedMax.getTime()) return true;
              if (isDateAllowed && !isDateAllowed(x)) return true;
              return false;
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

